import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchDropReviews, saveDropReviews } from '../api/dailyApi';
import { updatePriceManually } from '../api/api';
import { useStorage } from '../contexts/StorageContext';
import type { Card, PriceReviewDecision } from '../types/card';
import type { DropReviewList, DropReviewSaveItem } from '../types/daily';
import { appMessage as message } from '../utils/appMessage';
import { gatedRisePendingIds, pendingDropIds } from '../utils/daily';
import type { useInlinePriceEdit } from './UseInlinePriceEdit';

type BaseEdit = ReturnType<typeof useInlinePriceEdit>;

/** 한 번에 보내는 검토 줄 수 — 묶음 일괄 적용이 수백 건이어도 요청 하나가 너무 커지지 않게. */
const SAVE_CHUNK = 200;

/**
 * 하락 검토 · 상승 확인을 서버에(감사 card-controltower#566, 백엔드 PR #79 Q3).
 *
 * 예전엔 '하락 확인 대기'(pendingConfirmIds)가 브라우저 localStorage(UseDailyWorkflowStorage)에만 있어 다른 PC · 직원 · 다음 날엔
 * 몰랐고, ③ 은 그 목록을 프런트에서만 막았다. 이제 서버가 KEEP · APPLY(검토자 · 시각)를 카드에 남기고 ③ 이 '확인 필요' 하락 ·
 * 상승 이상치를 APPLY 없이는 건너뛴다. 이 훅은:
 *   - 서버 목록(GET /drop-reviews)의 검토 대기 하락을 하락 확인 대기로 맞춘다(로컬 표시를 서버 값으로 — 차이만 빼고 더한다).
 *   - 검토 동작(✓ 적용 · 일괄 적용 · ✗ 유지 · 일괄 유지)이 끝나면 PUT /drop-reviews 로 APPLY · KEEP 을 '화면이 본 수정가' 와 함께 남긴다.
 *   - 상승 이상치(③ 이 막는 상승) 검토를 연다(reviewRise).
 * 옛 서버(404)면 아무것도 하지 않는다 — 예전처럼 localStorage 만.
 *
 * useInlinePriceEdit 를 고치지 않고 감싼다(열린 PR 들과 같은 함수를 고치지 않게) — 반환값은 그 훅의 것에 serverReview 를 더한 것.
 */
export function useServerDropReviews(base: BaseEdit, opts: { activeJobId: string | null }) {
  const { activeJobId } = opts;
  const { store, superStore, isLoggedIn, isAdmin } = useStorage();
  const scope = `${isLoggedIn ? 'in' : 'out'}:${superStore ?? store ?? 'none'}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const [list, setList] = useState<DropReviewList | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);
  // 받은 뒤 이 화면에서 결정한 카드 — 결정 전에 시작한 조회가 늦게 와도 대기로 되돌리지 않는다.
  const decidedSinceFetchRef = useRef<Set<number>>(new Set());
  // '다시 검토' 로 대기에 되돌린 카드 — 서버엔 결정이 남아 있어도 다시 결정할 때까지 대기로 둔다.
  const restoredRef = useRef<Set<number>>(new Set());
  const baseRef = useRef(base);
  baseRef.current = base;

  const refresh = useCallback(async () => {
    const at = scopeRef.current;
    decidedSinceFetchRef.current = new Set();
    try {
      const l = await fetchDropReviews();
      if (at !== scopeRef.current) return;
      if (!l) { setSupported(false); return; }
      setSupported(true);
      setList(l);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (at === scopeRef.current && status === 404) setSupported(false);
    }
  }, []);

  useEffect(() => {
    setList(null);
    setSupported(null);
    restoredRef.current = new Set();
    if (scope.startsWith('in:') && !scope.endsWith(':none')) void refresh();
  }, [scope, refresh]);

  // 잡(가격 받기 · 분류 · ③)이 끝나면 다시 받는다 — 수정가가 바뀌면 서버 결정이 풀려 대기로 돌아온다.
  const prevJobRef = useRef(activeJobId);
  useEffect(() => {
    const prev = prevJobRef.current;
    prevJobRef.current = activeJobId;
    if (prev && !activeJobId && supported !== false) void refresh();
  }, [activeJobId, supported, refresh]);

  // 서버 목록 → 로컬 하락 확인 대기(차이만). 목록이 바뀔 때만 — 대기가 바뀔 때마다 돌면 결정 직후 되돌린다.
  useEffect(() => {
    if (!list) return;
    const b = baseRef.current;
    const server = new Set(pendingDropIds(list));
    const local = b.pendingConfirmIds;
    const toRemove = Array.from(local).filter((id) => !server.has(id) && !restoredRef.current.has(id));
    const toAdd = Array.from(server).filter((id) => !local.has(id) && !decidedSinceFetchRef.current.has(id));
    if (toRemove.length > 0) b.removePendingMany(toRemove);
    if (toAdd.length > 0) b.restorePendingMany(toAdd);
  }, [list]);

  /**
   * 검토를 서버에 남긴다 — 거절된 줄은 알리고 목록을 다시 받는다. 옛 서버 · 보기 전용은 조용히 건너뛴다.
   * 돌려주는 값 = 서버가 받은(ok) 카드 id(감사 #658 — 부른 쪽이 성공한 줄만 화면에 반영).
   * 묶음을 보내는 동안 매장 · 계정이 바뀌면 남은 묶음은 보내지 않는다(감사 #660 — 요청은 지금 매장 헤더로 나간다).
   */
  const report = useCallback(async (items: DropReviewSaveItem[]): Promise<Set<number>> => {
    const saved = new Set<number>();
    const valid = items.filter((i) => Number.isFinite(i.price) && i.price > 0);
    valid.forEach((i) => { decidedSinceFetchRef.current.add(i.cardId); restoredRef.current.delete(i.cardId); });
    if (valid.length === 0 || supported === false || !isAdmin) return saved;
    const at = scopeRef.current;
    const rejected: string[] = [];
    try {
      for (let i = 0; i < valid.length; i += SAVE_CHUNK) {
        if (at !== scopeRef.current) return saved;
        // eslint-disable-next-line no-await-in-loop
        const r = await saveDropReviews(valid.slice(i, i + SAVE_CHUNK));
        r.results.forEach((x) => {
          if (x.ok) saved.add(x.cardId);
          else rejected.push(x.message ?? `#${x.cardId}`);
        });
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (at !== scopeRef.current) return saved;
      if (status === 404) { setSupported(false); return saved; }
      message.warning({
        key: 'drop-review-save',
        content: '검토 결정을 서버에 남기지 못했습니다 — ③ 스토어 반영에서 이 카드가 건너뛰어질 수 있습니다. 목록을 다시 받아 확인하세요.',
        duration: 8,
      });
      void refresh();
      return saved;
    }
    if (at !== scopeRef.current) return saved;
    if (rejected.length > 0) {
      message.warning({
        key: 'drop-review-save',
        content: `검토 ${rejected.length}건을 서버가 받지 않았습니다 — ${rejected.slice(0, 2).join(' / ')}${rejected.length > 2 ? ' …' : ''}`,
        duration: 8,
      });
      void refresh();
    }
    return saved;
  }, [supported, isAdmin, refresh]);

  const decision = (cards: Card[], d: PriceReviewDecision, priceOf: (c: Card) => number | null): DropReviewSaveItem[] =>
    cards.map((c) => ({ cardId: c.id, decision: d, price: priceOf(c) ?? NaN }));

  // ✓ 적용 — 입력한 금액이면 저장 뒤 그 값이 수정가, 아니면 지금 수정가.
  const applyDrop = useCallback(async (card: Card, override?: number): Promise<boolean> => {
    const ok = await baseRef.current.applyDrop(card, override);
    if (ok) {
      const price = override != null && override !== card.suggestedPrice ? override : card.suggestedPrice;
      void report(decision([card], 'APPLY', () => price));
    }
    return ok;
  }, [report]);

  // ✗ 유지 — 수동 가격 저장(PATCH, 보호) 뒤 수정가 = 네이버 가격에 KEEP.
  const keepDrop = useCallback(async (card: Card): Promise<boolean> => {
    const ok = await baseRef.current.keepDrop(card);
    if (ok) void report(decision([card], 'KEEP', (c) => c.currentPrice));
    return ok;
  }, [report]);

  const applyDropsBulk = useCallback((cards: Card[]) => {
    baseRef.current.applyDropsBulk(cards);
    void report(decision(cards, 'APPLY', (c) => c.suggestedPrice));
  }, [report]);

  const keepDropsBulk = useCallback(async (cards: Card[]): Promise<boolean> => {
    const ok = await baseRef.current.keepDropsBulk(cards);
    if (ok) void report(decision(cards.filter((c) => c.currentPrice != null), 'KEEP', (c) => c.currentPrice));
    return ok;
  }, [report]);

  // '다시 검토' — 대기로 되돌리고, 서버 목록을 다시 받아도 다시 결정할 때까지 대기로 둔다.
  const restorePendingMany = useCallback((ids: number[]) => {
    ids.forEach((id) => { restoredRef.current.add(id); decidedSinceFetchRef.current.delete(id); });
    baseRef.current.restorePendingMany(ids);
  }, []);

  /**
   * 상승 이상치(상승률 50% 초과 · ③ 이 APPLY 없이 막는 상승) 검토 — APPLY 면 지금 수정가로, KEEP 이면 네이버 가격을 수동 저장(보호) 뒤 KEEP.
   * true = 서버에 남김. 서버가 받지 않았으면(거절 · 오류 · 매장 전환) 화면 상태를 바꾸지 않는다(감사 #658).
   */
  const reviewRise = useCallback(async (card: Card, d: PriceReviewDecision, replaceCard?: (c: Card) => void): Promise<boolean> => {
    if (d === 'KEEP') {
      if (card.currentPrice == null) return false;
      try {
        const saved = await updatePriceManually(card.id, card.currentPrice);
        replaceCard?.(saved);
      } catch {
        message.error('네이버 가격 유지 저장 중 오류가 발생했습니다.');
        return false;
      }
      if (!(await report(decision([card], 'KEEP', (c) => c.currentPrice))).has(card.id)) return false;
    } else if (!(await report(decision([card], 'APPLY', (c) => c.suggestedPrice))).has(card.id)) {
      return false;
    }
    setList((l) => (l ? {
      ...l,
      items: l.items.map((i) => (i.cardId === card.id ? { ...i, reviewState: d } : i)),
    } : l));
    return true;
  }, [report]);

  const gatedRisePending = useMemo(() => new Set(list ? gatedRisePendingIds(list) : []), [list]);
  const reviewById = useMemo(() => new Map((list?.items ?? []).map((i) => [i.cardId, i])), [list]);

  const serverReview = useMemo(() => ({
    /** 검토를 남길 수 있는가(ADMIN · SUPER). */
    canReview: isAdmin,
    /** null = 아직 모름, false = 옛 서버(검토는 이 브라우저에만). */
    supported,
    list,
    /** 카드 id → 서버 검토 줄. */
    reviewById,
    /** ③ 이 막는 상승 이상치 중 검토 대기. */
    gatedRisePending,
    refresh,
    reviewRise,
  }), [isAdmin, supported, list, reviewById, gatedRisePending, refresh, reviewRise]);

  return useMemo(() => ({
    ...base,
    applyDrop, keepDrop, applyDropsBulk, keepDropsBulk, restorePendingMany,
    serverReview,
  }), [base, applyDrop, keepDrop, applyDropsBulk, keepDropsBulk, restorePendingMany, serverReview]);
}
